import React from 'react';
import { Card, Descriptions, Spin, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getResidentById } from '@/api/residents';
import { useAuth } from '@/contexts/AuthContext';
import dayjs from 'dayjs';


interface ResidentTabProps {
  residentId: string;
}

const ResidentTab: React.FC<ResidentTabProps> = ({ residentId }) => {
  const { t } = useTranslation(['residents', 'common']);
  const { firestoreUser } = useAuth();

  const { data: resident, isLoading, error } = useQuery({
    queryKey: ['resident', residentId],
    queryFn: () => getResidentById(residentId, firestoreUser!),
    enabled: !!residentId && !!firestoreUser,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (error || !resident) {
    return (
      <Card>
        <Empty description={t('residents:notFound')} />
      </Card>
    );
  }

  return (
    <Card>
      <Descriptions bordered column={1}>
        <Descriptions.Item label={t('residents:fields.user')}>
          {resident.user?.displayName || resident.id}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('residents:fields.type')}>
          {t(`residents:residencyTypes.${resident.type}`)}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('residents:fields.status')}>
          {t(`residents:status.${resident.status}`)}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('residents:fields.moveInDate')}>
          {resident.moveInDate ? dayjs(resident.moveInDate).format('DD/MM/YYYY') : '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('residents:fields.moveOutDate')}>
          {resident.moveOutDate ? dayjs(resident.moveOutDate).format('DD/MM/YYYY') : '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('residents:fields.location.block')}>
          {resident.block?.name || '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('residents:fields.location.unit')}>
          {resident.apartmentUnit?.number || '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('residents:fields.contacts')}>
          {resident.contacts?.length ? `${resident.contacts.length} contact(s)` : '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('residents:fields.vehicles.type')}>
          {resident.vehicles?.length ? `${resident.vehicles.length} vehicle(s)` : '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('residents:fields.pets.name')}>
          {resident.pets?.length ? `${resident.pets.length} pet(s)` : '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('residents:fields.familyMembers.name')}>
          {resident.familyMembers?.length ? `${resident.familyMembers.length} family member(s)` : '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('residents:fields.notes')}>
          {resident.notes || '-'}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

export default ResidentTab; 